import { FINISHED_TO_ORDER } from '@consts/index'
import { filterOutSomeLowerLegVariants } from '@helpers/filters'
import { BackendColourwayStock, BackendStockSku } from 'types/backendPdp'
import { FulfillmentLocation, ProductWithVariants } from 'types/product'

const LEG = 'Leg'

type LocatedSku = BackendStockSku & {
  fulfillment_location: FulfillmentLocation
}

// CAS can name Torque for a finished leg Torque doesn't hold, which sends the
// order to TAL with no Made to Order messaging. The backend's location is the
// one Shopify routes by, so it wins wherever the backend lists the SKU.
export function withBackendLocations<T extends ProductWithVariants>(
  product: T,
  stock: BackendColourwayStock | undefined
): T {
  if (!stock || !product.variants) return product

  const locations = new Map(
    stock.skus.filter(isLocated).map(sku => [sku.code, sku])
  )

  return {
    ...product,
    variants: product.variants.map(variant => {
      const sku = locations.get(variant.sku)
      return sku
        ? {
            ...variant,
            fulfillment_location: sku.fulfillment_location,
            tal_dispatch_days: sku.tal_dispatch_days
          }
        : variant
    })
  }
}

// The SKU the basket should add for a suggested size: the suggested SKU, or,
// for a leg, whichever of its finished leg and Finished to Order Torque holds,
// so the order isn't sent to TAL while Torque can hem one.
export function findSkuToAdd(
  skus: BackendStockSku[],
  suggestedCode: string,
  leg?: string
): LocatedSku | undefined {
  const suggested = skus.find(sku => sku.code === suggestedCode)
  if (!suggested) return undefined

  const sameSize =
    leg && suggested.options[LEG]
      ? skus.filter(sku => isSameSizeAtLeg(sku, suggested, leg))
      : [suggested]
  const buyable = sameSize
    .filter(isLocated)
    .filter(sku => sku.shopify_variant_gid)
    .map(sku => ({ ...sku, sku: sku.code }))

  return filterOutSomeLowerLegVariants(buyable, leg).sort(
    (first, second) => preference(first) - preference(second)
  )[0]
}

function preference(sku: LocatedSku) {
  const atTal = sku.fulfillment_location === 'TAL' ? 2 : 0
  const finishedToOrder = sku.options[LEG] === FINISHED_TO_ORDER ? 1 : 0

  return atTal + finishedToOrder
}

function isSameSizeAtLeg(
  sku: BackendStockSku,
  suggested: BackendStockSku,
  leg: string
) {
  return (
    [leg, FINISHED_TO_ORDER].includes(sku.options[LEG]) &&
    Object.entries(suggested.options).every(
      ([name, value]) => name === LEG || sku.options[name] === value
    )
  )
}

function isLocated(sku: BackendStockSku): sku is LocatedSku {
  return (
    sku.fulfillment_location === 'Torque' || sku.fulfillment_location === 'TAL'
  )
}
